import React from 'react';
import { cn } from '../../../../../../lib/utils';
import { PipeMillProjectLimit, PipeMillReportType, PipeMillStatus } from './pipeMillReportsApi';

export const PIPE_PAGE_REPORT_TYPES: PipeMillReportType[] = ['HYDROSTATIC', 'DIMENSIONAL', 'VISUAL'];

export const REPORT_TYPE_LABELS: Record<PipeMillReportType, string> = {
  CHEMICAL: 'Chemical',
  TENSILE: 'Tensile',
  CVN: 'CVN',
  AUT: 'AUT',
  HYDROSTATIC: 'Hydrostatic',
  DIMENSIONAL: 'Dimensional',
  HARDNESS: 'Hardness',
  FLATTENING: 'Flattening',
  BEND: 'Bend',
  VISUAL: 'Visual',
  RT: 'RT',
  MPT: 'MPT',
  HEAT_TREAT: 'Heat Treat',
  PIPE_BODY_UT: 'Pipe Body UT',
  MFL_ET: 'MFL / ET',
  COATING: 'Coating',
  THREAD: 'Thread',
  WELD_MAP: 'Weld Map',
};

export const valueOrDash = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

export const formatLabel = (key: string): string =>
  key
    .replace(/^pipe/, '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const statusClassName = (status?: string): string => cn(
  'rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wide',
  status === 'PASS' && 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
  status === 'FAIL' && 'text-readable-red bg-[rgba(196,0,31,0.06)]',
  status === 'PENDING_REVIEW' && 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
  status === 'CONDITIONAL' && 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
  status === 'INCOMPLETE' && 'text-heritage-grey bg-[#EEF0F3]',
  !status && 'text-heritage-grey bg-[#EEF0F3]'
);

export const StatusBadge: React.FC<{ status?: PipeMillStatus | string }> = ({ status }) => (
  <span className={statusClassName(status)}>{status || 'Not Started'}</span>
);

export const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-lg border border-[#EEF0F3] bg-white p-3">
    <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{label}</div>
    <div className="mt-1 text-[13px] font-medium text-text-grey break-words">{value || '-'}</div>
  </div>
);

export const getMaterialPipeLengthM = (pipeLengthInches?: unknown, fallbackM = 12.18): number => {
  const lengthInches = Number(pipeLengthInches);
  if (Number.isFinite(lengthInches) && lengthInches > 0) {
    return lengthInches * 0.0254;
  }
  return fallbackM;
};

export const getMaterialPipeLengthMm = (pipeLengthInches?: unknown): number | undefined => {
  const lengthInches = Number(pipeLengthInches);
  if (Number.isFinite(lengthInches) && lengthInches > 0) {
    return lengthInches * 25.4;
  }
  return undefined;
};

export const createDefaultFormValues = (reportType: PipeMillReportType, selectedPipe: any, details: any): Record<string, any> => {
  const odMm = selectedPipe?.size ? Number(selectedPipe.size) * 25.4 : undefined;
  const wtMm = selectedPipe?.wallThickness ? Number(selectedPipe.wallThickness) * 25.4 : undefined;
  const lengthM = getMaterialPipeLengthM(details?.pipeLength);

  if (reportType === 'HYDROSTATIC') {
    return {
      test_pressure_bar: '',
      hold_duration_s: '',
      test_medium: 'water',
      pressure_drop_obs: false,
      leak_observed: false,
      sealing_method: 'end_cap',
      gauge_cal_ref: '',
      design_factor: '',
    };
  }

  if (reportType === 'DIMENSIONAL') {
    return {
      specified_od_mm: odMm ? Number(odMm.toFixed(3)) : '',
      specified_wt_mm: wtMm ? Number(wtMm.toFixed(3)) : '',
      od_measurements: '',
      wt_measurements: '',
      pipe_length_m: lengthM ? Number(lengthM.toFixed(3)) : '',
      straightness_mm: '',
      bevel_angle_deg: '',
      end_squareness_mm: '',
    };
  }

  return {
    inspection_coverage: 'external',
    lighting_lux: '',
    inspector: {
      cert_level: '',
    },
    surface_ok_external: true,
    surface_ok_internal: true,
    defects: [],
  };
};

export const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const toNumberArray = (value: unknown): number[] => {
  if (Array.isArray(value)) return value.map(Number).filter((item) => !Number.isNaN(item));
  return String(value || '')
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item) => !Number.isNaN(item));
};

export const getNestedValue = (values: Record<string, any>, path: string) => {
  return path.split('.').reduce((current, key) => current?.[key], values);
};

export const setNestedValue = (values: Record<string, any>, path: string, value: unknown) => {
  const keys = path.split('.');
  const next = { ...values };
  let cursor: Record<string, any> = next;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }

    cursor[key] = { ...(cursor[key] || {}) };
    cursor = cursor[key];
  });

  return next;
};

export const buildReportData = (reportType: PipeMillReportType, values: Record<string, any>): Record<string, unknown> => {
  if (reportType === 'HYDROSTATIC') {
    return {
      test_pressure_bar: Number(values.test_pressure_bar),
      hold_duration_s: Number(values.hold_duration_s),
      test_medium: values.test_medium,
      pressure_drop_obs: Boolean(values.pressure_drop_obs),
      leak_observed: Boolean(values.leak_observed),
      sealing_method: values.sealing_method,
      gauge_cal_ref: values.gauge_cal_ref,
      ...(values.design_factor ? { design_factor: Number(values.design_factor) } : {}),
    };
  }

  if (reportType === 'DIMENSIONAL') {
    return {
      specified_od_mm: Number(values.specified_od_mm),
      specified_wt_mm: Number(values.specified_wt_mm),
      od_measurements: toNumberArray(values.od_measurements),
      wt_measurements: toNumberArray(values.wt_measurements),
      pipe_length_m: Number(values.pipe_length_m),
      straightness_mm: Number(values.straightness_mm),
      bevel_angle_deg: Number(values.bevel_angle_deg),
      end_squareness_mm: Number(values.end_squareness_mm),
    };
  }

  return {
    inspection_coverage: values.inspection_coverage,
    lighting_lux: Number(values.lighting_lux),
    inspector: {
      cert_level: values.inspector?.cert_level,
    },
    surface_ok_external: Boolean(values.surface_ok_external),
    surface_ok_internal: Boolean(values.surface_ok_internal),
    defects: Array.isArray(values.defects) ? values.defects.map((defect: any) => ({
      type: defect.type,
      pos_mm: Number(defect.pos_mm),
      depth_pct_wt: Number(defect.depth_pct_wt),
      disposition: defect.disposition,
    })) : [],
  };
};

export const hydrateFormValuesFromReport = (
  reportType: PipeMillReportType,
  _selectedPipe: any,
  _details: any,
  reportData?: Record<string, any>
): Record<string, any> => {
  if (!reportData) return {};

  if (reportType === 'DIMENSIONAL') {
    return {
      ...reportData,
      od_measurements: Array.isArray(reportData.od_measurements) ? reportData.od_measurements.join(', ') : reportData.od_measurements || '',
      wt_measurements: Array.isArray(reportData.wt_measurements) ? reportData.wt_measurements.join(', ') : reportData.wt_measurements || '',
    };
  }

  if (reportType === 'VISUAL') {
    return {
      ...reportData,
      inspector: {
        ...(reportData.inspector || {}),
      },
      defects: Array.isArray(reportData.defects) ? reportData.defects : [],
    };
  }

  return {
    ...reportData,
  };
};

export const getLimitValueDisplay = (limit: PipeMillProjectLimit): string => {
  const parts = [];
  if (limit.minValue !== undefined && limit.minValue !== null) parts.push(`Min ${limit.minValue}`);
  if (limit.maxValue !== undefined && limit.maxValue !== null) parts.push(`Max ${limit.maxValue}`);
  if (limit.expectedValue) parts.push(`Expected ${limit.expectedValue}`);
  return parts.length > 0 ? parts.join(' / ') : '-';
};
